/* global Word console Office */

import { WORD_FILE_TIMEOUT_MS, WORD_RUN_TIMEOUT_MS, withTimeout } from "../utils/timeout";

export async function insertText(text: string) {
  // Write text to the document.
  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      body.insertParagraph(text, Word.InsertLocation.end);
      await context.sync();
    });
  } catch (error) {
    console.error("insertText error:", error);
  }
}

async function openDocumentFile(): Promise<Office.File> {
  return new Promise<Office.File>((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 65536 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error);
        }
      }
    );
  });
}

async function readSlice(file: Office.File, index: number): Promise<Uint8Array> {
  return new Promise<Uint8Array>((resolve, reject) => {
    file.getSliceAsync(index, (sliceResult) => {
      if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
        // data is typed `any` by @types/office-js; for FileType.Compressed
        // Office returns a number[] or Uint8Array depending on the host version.
        resolve(new Uint8Array(Array.from(sliceResult.value.data as ArrayLike<number>)));
      } else {
        reject(sliceResult.error);
      }
    });
  });
}

async function readFileSlices(file: Office.File): Promise<Blob> {
  const slices: Uint8Array[] = [];
  for (let i = 0; i < file.sliceCount; i++) {
    slices.push(await readSlice(file, i));
  }
  const totalLength = slices.reduce((sum, s) => sum + s.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const slice of slices) {
    combined.set(slice, offset);
    offset += slice.length;
  }
  return new Blob([combined], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}

function closeOfficeFile(file: Office.File): Promise<void> {
  return new Promise<void>((resolve) => {
    file.closeAsync(() => {
      resolve();
    });
  });
}

export async function getDocumentFile(): Promise<Blob | null> {
  // Office allows at most 2 open File handles. Close even if withTimeout
  // rejects while getFileAsync is still running.
  let file: Office.File | null = null;
  const openPromise = openDocumentFile().then((opened) => {
    file = opened;
    return opened;
  });
  try {
    const opened = await withTimeout(openPromise, WORD_FILE_TIMEOUT_MS, "getFileAsync timed out");
    return await withTimeout(
      readFileSlices(opened),
      WORD_FILE_TIMEOUT_MS,
      "readFileSlices timed out"
    );
  } catch (error) {
    console.error("getDocumentFile error:", error);
    return null;
  } finally {
    if (file != null) {
      await closeOfficeFile(file);
    } else {
      void openPromise.then((opened) => closeOfficeFile(opened)).catch(() => undefined);
    }
  }
}

export async function getDocumentText(): Promise<string | null> {
  // Returns `null` ONLY on Word.run failure/timeout so callers can distinguish
  // "Word is broken — don't overwrite S3" from "the user cleared the document".
  try {
    return await withTimeout(
      Word.run(async (context) => {
        const body = context.document.body;
        context.load(body, "text");
        await context.sync();
        return body.text;
      }),
      WORD_RUN_TIMEOUT_MS,
      "Word.run timed out in getDocumentText"
    );
  } catch (error) {
    console.error("getDocumentText error:", error);
    return null;
  }
}
